import { useCallback, useEffect, useState } from 'react';
import { familyConversationsApi, familyTransport } from '../api/conversations';
import type { ConversationDetail } from '../api/conversations';
import { familyEventsApi } from '../api/events';
import type { EventDetail } from '../api/events';
import { loadEventTopics } from '../api/eventTopics';
import type { EventTopics } from '../api/eventTopics';
import { familyApi } from '../api/auth';
import type { Child } from '../api/auth';
import { EventDocket } from './EventDocket';
import { EventRecord } from './EventRecord';
import { EventThread } from './EventThread';
import { AppBar, Footer, Gutter, Page } from '../ui/Chrome';
import type { Me } from '../api/auth';

/**
 * An event, read by a parent.
 *
 * The same event a member sees, fetched through `/family` because a guardian holds no membership
 * and `/units/` would 404 them (TROOP-66). Deliberately **not** a family-flavoured version of the
 * event: the facts are the facts, and since TROOP-116 they are literally the same
 * {@link EventRecord} and the same {@link EventDocket} the member's page renders.
 *
 * **That sharing is the fix, not a tidy-up.** The two screens had drifted apart in ways nobody
 * chose: this one said Starts/Ends/Leaves/Back where the member's said Depart/Return, dropped the
 * event-type eyebrow, dropped the foreign-zone notice — so a parent reading about a campout from a
 * work trip in Denver was shown the troop's clock with nothing saying so — collapsed the
 * cancellation block to one line, and offered a button labelled "Open the thread" where the member
 * had the conversation itself. Rule 13b says `/family` mirrors `/units` in routes and never in
 * rules; it should not have been mirroring it in *layout* either, badly.
 *
 * What genuinely differs is what a parent can *do*, and that is not a reduced set — it is a
 * different one. They answer for each of their children, they pack their children's bags, they
 * offer a car, they upload the photographs they took. What they cannot do is edit or cancel: that
 * is an officer's job, and those controls do not render rather than rendering disabled, because a
 * disabled control owes an explanation and "you are not an officer of this troop" is not something
 * a parent can act on.
 */
export function FamilyEventScreen({
  eventId,
  me,
  meId,
  onBack,
  onNavigate,
  onOpenThread,
}: {
  eventId: string;
  /** The caller's own person id, so they can answer for themselves before any row exists. */
  /** The signed-in person, for the account control the bar now carries. */
  me: Me;
  meId: string;
  onBack: () => void;
  /** For the account control the bar carries. Not the same as onBack or onOpenThread. */
  onNavigate: (path: string) => void;
  onOpenThread: (conversationId: string) => void;
}) {
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [thread, setThread] = useState<ConversationDetail | null>(null);
  const [topics, setTopics] = useState<EventTopics | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [detail, mine] = await Promise.all([
        familyEventsApi.detail(eventId),
        familyApi.children(),
      ]);

      setEvent(detail);
      setChildren(mine);
      setLoadError(null);

      if (detail.conversationId) {
        familyConversationsApi
          .detail(detail.conversationId)
          .then(setThread)
          .catch(() => undefined);
      }

      // The guardian's door for the same list (TROOP-179), and it is not decoration: an event's
      // parents-and-leaders topic is exactly the one a parent came for, and it is unreachable
      // under /units/ because they hold no membership. Same method behind it, so the two doors
      // cannot answer differently (rule 13b).
      loadEventTopics(undefined, eventId)
        .then(setTopics)
        .catch(() => undefined);
    } catch {
      // A 404 covers "no such event" and "not yours" alike, so the screen says the one true thing
      // it knows rather than guessing which.
      setLoadError('Could not load this event.');
    }
  }, [eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Narrowed to this event's unit. A parent with children in a troop and a pack should not be
  // offered the pack's Cub on the troop's campout.
  const here = children.filter((c) => event && c.unitNames.includes(event.unit.name));

  return (
    <Page>
      <AppBar
        parentLabel="Your family"
        title={event?.title ?? 'Event'}
        subtitle={event?.unit?.name}
        onBack={onBack}
        me={me}
        onNavigate={onNavigate}
      />

      <section className="rule-canonical" style={{ background: 'var(--color-surface-page)' }}>
        <Gutter>
          {loadError && (
            <p
              className="t-body"
              role="alert"
              style={{ color: 'var(--color-text-secondary)', paddingBlock: 'var(--space-6)' }}
            >
              {loadError}
            </p>
          )}

          {event && (
            <>
              <EventRecord event={event} />

              {/* No unitId: its absence is what picks the guardian's door throughout the docket,
                  exactly as it does for photos and transport. No subgroup labels either -- they
                  are EventPacking's, and a parent gets FamilyPacking.

                  And no edit control: a guardian is not an officer, so it is absent rather than
                  disabled. A disabled control owes an explanation, and "you are not an officer of
                  this troop" is not something a parent can act on. */}
              <EventDocket
                event={event}
                meId={meId}
                children={here}
                onEventChanged={() => void load()}
              />

              <div style={{ paddingBottom: 'var(--space-6)' }} />
            </>
          )}
        </Gutter>
      </section>

      <section
        className="flex-1"
        style={{ background: 'var(--color-surface-sunken)', paddingBottom: 'var(--space-6)' }}
      >
        {event && (
          <EventThread
            transport={familyTransport}
            conversationId={event.conversationId}
            detail={thread}
            meId={meId}
            topics={topics?.rows}
            // No onOpenTopics: a guardian holds no office, and opening a topic on the unit's
            // event is an organising act. Absent rather than disabled -- there is no explanation
            // to give a parent that they could act on.
            onOpenThread={onOpenThread}
          />
        )}
      </section>

      <Footer />
    </Page>
  );
}
